import { useEffect, useMemo, useRef, useState } from "react";
import {
  CERTIFIED_LOGO_MAX_HEIGHT,
  CERTIFIED_LOGO_MAX_WIDTH,
} from "@shared/app-certification";
import { PAGE_X, PAGE_Y } from "../_shared/safeArea";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import { Logo } from "../_shared/Logo";
import { RemoteHint, type RemoteHintItem } from "../_shared/RemoteHint";
import { useNow, formatDate, formatTime } from "../_shared/useNow";
import {
  useFocusGrid,
  useFocusNav,
  useScreenFocus,
  type NavMap,
} from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { homeBgLayers } from "../_shared/bg";
import { useRadioNowPlaying } from "../_shared/useRadioNowPlaying";
import { useRadioPlayback } from "../_shared/useRadioPlayback";
import type { SchemeTokens } from "@shared/types/foyer";
import {
  SchemeProvider,
  useScheme,
  screenTitle,
  screenTitleOverride,
  visibleHomeSections,
  stationHue,
  type SchemeContextValue,
  type ResolvedApp,
} from "../_shared/schemeContext";
import { launchApp } from "../../lib/launchApp";
import { openTvForThisScreen } from "../../lib/openTv";
import { bridge } from "../../lib/foyerBridge";
import { castSteps } from "../../lib/casting";
import { SchemeSlot } from "../_shared/slots/SchemeSlot";
import CustomScreen from "../_shared/custom/CustomScreen";
import OffersEntry from "../_shared/offers/OffersEntry";
import VodScreen, { type VodScreenStyle } from "../_shared/vod/VodScreen";
import BuiltInPlayer from "../_shared/tv/BuiltInPlayer";
import GuideScreen from "../_shared/guide/GuideScreen";
import { useOfferSlot } from "../_shared/slots/offerSlot";
import type { SchemeProps } from "../registry";
import { mosaicManifest } from "@shared/schemes/manifests";

// DEMO-1: TV + Screen Share, ported to Atelier/Mosaic/Spectrum from the
// launcher templates (`_shared/launcher/kit.tsx`). See SpectrumScheme.tsx for
// the full reasoning on why this is duplicated rather than imported.
//
// TV-6: the TV package name is NO LONGER duplicated here. Where the tile sends
// the screen - the vendor streaming app, or Foyer's own player when the premise
// has been switched to it - is decided in one place, `lib/openTv.ts`.

function tryOpenScreenShare(): boolean {
  const info = bridge.getCastingInfo();
  if (!info.supported || !info.launchable) return false;
  return bridge.launchCastReceiver();
}

/**
 * Mosaic - Scandi-modern bento. Saturated colour-blocked tiles,
 * Instrument Serif + DM Sans, brick-red italic em emphasis, micro-tilt
 * focus on app cards, off-canvas oversized vinyl, HSL-tinted radio.
 * Ported from `direction-mosaic.jsx`.
 */

/** Reconstruct Mosaic's palette object from resolved design tokens. Brick-red
 *  is the primary accent; the other named blocks live in token `extras`. */
function mosaicPalette(t: SchemeTokens) {
  const e = { ...mosaicManifest.defaultTokens.extras, ...t.extras };
  return {
    paper: t.palette.bg,
    paper2: e.paper2,
    ink: t.palette.ink,
    taupe: t.palette.inkDim ?? "",
    rule: t.palette.rule ?? "",
    navy: e.navy,
    cream: e.cream,
    butter: e.butter,
    sage: e.sage,
    blush: e.blush,
    brick: t.palette.accent,
    plum: e.plum,
    ocean: e.ocean,
    serif: t.fonts.serif ?? "",
    sans: t.fonts.sans ?? "",
    mono: t.fonts.mono ?? "",
  };
}
function useM() {
  return mosaicPalette(useScheme().tokens);
}

type Screen =
  | "home"
  | "apps"
  | "info"
  | "radio"
  | "hotel"
  | "custom"
  | "offers"
  // Foyer's own player (TV-3a) - distinct from the "TV" tile's launch action.
  // Foyer's own television — the shell the guide and the library live inside.
  // The TV TILE is still a launch action; which application it opens is a
  // premise setting (`tvPlayer`). This is what it opens when that setting
  // names Foyer.
  | "tv"
  | "vod"
  | "guide"
  | "cast";

const FOOTER_KEYS: RemoteHintItem[] = [
  { key: "◄ ►", label: "Move" },
  { key: "OK", label: "Open" },
  { key: "←", label: "Back" },
];

export default function MosaicScheme({ scheme }: SchemeProps) {
  const [screen, setScreen] = useTrackedScreen<Screen>("home");
  // Which offer the home banner asked for, if any (`POS-4`) - see AtelierScheme
  // for why it is held beside the screen rather than inside it.
  const [offerId, setOfferId] = useState<string | null>(null);
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  const go = (s: Screen) => {
    setOfferId(null);
    setScreen(s);
  };
  const openOffer = (id: string) => {
    setOfferId(id);
    setScreen("offers");
  };
  useBackHome(() => go("home"));
  const M = mosaicPalette(scheme.tokens);
  const { context } = scheme;
  // The tile that always promised this ("Order to the room · book the spa ·
  // skip the desk") finally keeps its word - as long as there is a shop behind
  // it. With none, it is exactly the screen it was.
  const hasOffers = (scheme.offers?.offers.length ?? 0) > 0;
  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: M.paper,
          color: M.ink,
          fontFamily: M.sans,
          outline: "none",
        }}
      >
        {screen === "home" && (
          <Home
            go={go}
            openOffer={openOffer}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "apps" && (
          <Apps go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "info" && (
          <Info go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "radio" && (
          <Radio go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "custom" && (
          <MosaicCustom go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "offers" && (
          <MosaicOffers
            go={go}
            offerId={offerId}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "tv" && (
          <BuiltInPlayer
            style={{ ...playerStyle(M), bg: M.paper }}
            onExit={() => go("home")}
            scopeRef={scopeRef}
          />
        )}
        {screen === "vod" && (
          <MosaicPlayer
            kind="vod"
            go={go}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "guide" && (
          <MosaicPlayer
            kind="guide"
            go={go}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "hotel" &&
          (hasOffers ? (
            <MosaicOffers go={go} scopeRef={scopeRef} context={context} />
          ) : (
            <HotelLaunch go={go} scopeRef={scopeRef} context={context} />
          ))}
        {screen === "cast" && (
          <Cast go={go} scopeRef={scopeRef} context={context} />
        )}
      </div>
    </SchemeProvider>
  );
}

function Header({ crumb }: { crumb?: string }) {
  const M = useM();
  const now = useNow();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `${PAGE_Y}px ${PAGE_X}px 14px`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <Logo variant="dark" height={22} />
        {crumb && (
          <>
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: M.brick,
              }}
            />
            <span
              style={{
                fontFamily: M.serif,
                fontStyle: "italic",
                fontSize: 22,
                color: M.ink,
              }}
            >
              {crumb}
            </span>
          </>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
        <span
          style={{
            fontFamily: M.serif,
            fontStyle: "italic",
            fontSize: 18,
            color: M.taupe,
          }}
        >
          {formatDate(now)}
        </span>
        <span
          style={{
            fontFamily: M.serif,
            fontSize: 28,
            color: M.ink,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatTime(now)}
        </span>
      </div>
    </div>
  );
}

function Footer({
  items,
  context,
}: {
  items: RemoteHintItem[];
  context: SchemeContextValue;
}) {
  const M = useM();
  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: `14px ${PAGE_X}px ${PAGE_Y}px`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        color: M.taupe,
      }}
    >
      <RemoteHint
        items={items.map((i) => ({
          ...i,
          bg: M.paper2,
          fg: M.ink,
          border: `0.5px solid ${M.rule}`,
        }))}
      />
      <span
        style={{
          fontFamily: M.serif,
          fontStyle: "italic",
          fontSize: 14,
        }}
      >
        {context.hotelName}
      </span>
    </div>
  );
}

interface ScreenProps {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}

/**
 * The premise's own page. The template lends the bar its accent and font and
 * owns nothing else - the operator's picture IS the page (see CustomScreen).
 */
function MosaicCustom({ go, scopeRef }: ScreenProps) {
  const M = useM();
  return (
    <CustomScreen
      scopeRef={scopeRef}
      onBack={() => go("home")}
      style={{
        barBg: "rgba(26,29,61,0.5)",
        ink: M.paper,
        accent: M.brick,
        onAccent: M.paper,
        font: M.sans,
      }}
    />
  );
}

/**
 * Room offers, in Mosaic's chrome. The shared renderer (see OffersScreen) inside
 * the template's header and footer, lent the brick accent and the Instrument
 * Serif display face.
 */
function MosaicOffers({
  go,
  scopeRef,
  context,
  offerId,
}: ScreenProps & { offerId?: string | null }) {
  const M = useM();
  const scheme = useScheme();
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb={screenTitle(scheme, "offers", "Offers")} />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          padding: `10px ${PAGE_X}px 70px`,
        }}
      >
        <OffersEntry
          offerId={offerId}
          scopeRef={scopeRef}
          onBack={() => go("home")}
          title={screenTitleOverride(scheme, "offers")}
          pad={{ x: 0, y: 0 }}
          hints={false}
          style={{
            bg: "transparent",
            card: M.paper2,
            ink: M.ink,
            inkDim: M.taupe,
            rule: M.rule,
            accent: M.brick,
            onAccent: M.paper,
            titleFont: M.serif,
            font: M.sans,
            radius: 18,
            placeholder: M.cream,
          }}
        />
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

/**
 * Foyer's own player, in Mosaic's chrome (`TV-3a`): the shared renderers inside
 * the template's header and footer, lent the brick accent and the Instrument
 * Serif display face. One component for both, because the template's answer to
 * both is the same set of tokens.
 */

/**
 * This template's palette as a `VodScreenStyle` — the one shape every player
 * surface speaks (`VodScreen`, `GuideScreen`, `TvShell`).
 *
 * Named once because it is now read twice: by the inset sub-pages, which sit
 * on the template's photograph and so want `bg: "transparent"`, and by
 * `BuiltInPlayer`, which REPLACES that photograph and overrides `bg` with the
 * template's own surface.
 */
function playerStyle(M: ReturnType<typeof useM>): VodScreenStyle {
  return {
    bg: "transparent",
    card: M.paper2,
    ink: M.ink,
    inkDim: M.taupe,
    rule: M.rule,
    accent: M.brick,
    onAccent: M.paper,
    titleFont: M.serif,
    font: M.sans,
    radius: 18,
    placeholder: M.cream,
  };
}

function MosaicPlayer({
  kind,
  go,
  scopeRef,
  context,
}: ScreenProps & { kind: "vod" | "guide" }) {
  const M = useM();
  const scheme = useScheme();
  const fallback = kind === "vod" ? "Films & series" : "TV guide";
  const style = playerStyle(M);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb={screenTitle(scheme, kind, fallback)} />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          padding: `10px ${PAGE_X}px 70px`,
        }}
      >
        {kind === "vod" ? (
          <VodScreen
            scopeRef={scopeRef}
            onBack={() => go("home")}
            title={screenTitleOverride(scheme, "vod")}
            pad={{ x: 0, y: 0 }}
            hints={false}
            style={style}
          />
        ) : (
          <GuideScreen
            scopeRef={scopeRef}
            onBack={() => go("home")}
            title={screenTitleOverride(scheme, "guide")}
            pad={{ x: 0, y: 0 }}
            hints={false}
            style={style}
          />
        )}
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

/**
 * How the right-hand region of the bento breaks into rows, as index groups.
 *
 * ONE source for both {@link destSpan} and the home screen's D-pad graph, so the
 * focus ring cannot disagree with the layout. It used to be two, and above three
 * destinations the two quietly stopped agreeing: the spans overflowed the
 * four-column region and the nav map had no entry for a fourth tile at all,
 * which made it unreachable by remote. Four was already possible before Offers
 * existed (apps + info + radio + custom); Offers made five, and TV-3a's `vod` +
 * `guide` make seven. The function is general in `n`, which is the point - it
 * must not need another edit the next time a screen joins the menu.
 */
function destRows(n: number): number[][] {
  if (n <= 2) return Array.from({ length: n }, (_, i) => [i]);
  if (n === 3) return [[0], [1, 2]];
  if (n === 4) {
    return [
      [0, 1],
      [2, 3],
    ];
  }
  // Five or more: pairs, led by one full-width tile when the count is odd.
  const rows: number[][] = [];
  let i = 0;
  if (n % 2 === 1) {
    rows.push([0]);
    i = 1;
  }
  for (; i < n; i += 2) rows.push([i, i + 1].filter((k) => k < n));
  return rows;
}

/** Span for a destination tile in the right region of the bento, by slot. The
 *  region is 4 columns × 6 rows; a row of one fills the width, a row of two
 *  splits it. With the default [apps, info, radio] this reproduces the original
 *  bento (apps wide on top, info/radio side by side below). */
function destSpan(idx: number, n: number) {
  const rows = destRows(n);
  const found = rows.findIndex((row) => row.includes(idx));
  const row = rows[found] ?? [idx];
  return {
    gridColumn: `span ${Math.max(1, Math.floor(4 / row.length))}`,
    gridRow: `span ${Math.max(1, Math.floor(6 / rows.length))}`,
  };
}

/** The promo banner's id in the home d-pad graph. Not a screen id - it opens an
 *  offer, not a page, and must never collide with one. */
const PROMO_CELL = "homePromo";

function Home({
  go,
  openOffer,
  scopeRef,
  context,
}: ScreenProps & { openOffer: (offerId: string) => void }) {
  const M = useM();
  const scheme = useScheme();
  const clock = useNow();
  // A live offer in the promo slot earns a cell in the bento's d-pad graph.
  const banner = useOfferSlot(PROMO_CELL, clock);
  const hasBanner = !!banner;
  // Premise background image: Mosaic has no dedicated photo slot, so the hero
  // photo fills the large navy "Hotel" bento tile - the layout's visual anchor -
  // under a navy scrim that keeps its white copy legible. Honours
  // content.background.mode (`color` keeps the solid navy tile).
  const hero = scheme.content.images.hero;
  const bgL = homeBgLayers(scheme.content.background?.mode);
  const hotelPhoto = bgL.photo && hero ? hero : undefined;
  const DEST_DEF: Record<
    string,
    { label: string; sub: string; bg: string; fg: string; figure: string }
  > = {
    // TV is the fixed anchor - always first, never toggleable, same rule the
    // launcher templates and Care follow (DEMO-1).
    tv: {
      label: "TV",
      sub: "Live channels · the remote",
      bg: M.ocean,
      fg: M.paper,
      figure: "▶",
    },
    cast: {
      label: "Screen Share",
      sub: "Mirror a phone or laptop",
      bg: M.cream,
      fg: M.ink,
      figure: "❏",
    },
    apps: {
      label: "Apps",
      sub: "Netflix · Prime · YouTube · 9 more",
      bg: M.butter,
      fg: M.ink,
      figure: "▸",
    },
    info: {
      label: "Information",
      sub: "Wi-Fi, dining and the floor plan",
      bg: M.sage,
      fg: M.ink,
      figure: "✦",
    },
    radio: {
      label: "Radio",
      sub: "12 stations on air",
      bg: M.brick,
      fg: M.paper,
      figure: "◐",
    },
    // The premise's own page. `label` is the fallback only - the name comes
    // from the `customTitle` knob, applied below.
    custom: {
      label: "Custom",
      sub: "A page of our own",
      bg: M.plum,
      fg: M.paper,
      figure: "❖",
    },
    // Room offers. Same rule: the name comes from `offersTitle`. A screen the
    // manifest lists in `homeOrder.items` and this table does not is a crash,
    // which is the loud version of a tile that opens nothing.
    offers: {
      label: "Offers",
      sub: "Order in · book the spa",
      bg: M.blush,
      fg: M.ink,
      figure: "✻",
    },
    // Foyer's own player. Same rule again: the names come from `vodTitle` /
    // `guideTitle`, applied below.
    vod: {
      label: "Films & series",
      sub: "Watch here, now",
      bg: M.plum,
      fg: M.paper,
      figure: "▤",
    },
    guide: {
      label: "TV guide",
      sub: "What is on tonight",
      bg: M.ocean,
      fg: M.paper,
      figure: "▦",
    },
  };
  const visible = visibleHomeSections(scheme);
  // TV first always, then Screen Share when its toggle is on, then the
  // content-driven dests - same fixed-anchor rule as the launcher templates
  // (DEMO-1). Neither is a member of `homeOrder`: like Hotel, they're handled
  // here rather than through the generic order knob. Feeding them through the
  // SAME `destRows`/`destSpan` math as the content dests (rather than hand-
  // positioning two more tiles) is what keeps the bento's span/nav math
  // correct for any count.
  const showCast = scheme.knobs.has("showCast")
    ? scheme.knobs.toggle("showCast")
    : true;
  const anchorIds = showCast ? ["tv", "cast"] : ["tv"];
  const destIds = [...anchorIds, ...visible];
  const tiles = [
    {
      id: "hotel",
      label: "Hotel",
      sub: "Order to the room · book the spa · skip the desk",
      bg: M.navy,
      fg: M.paper,
      figure: "✿",
      go: "hotel" as Screen,
      span:
        destIds.length === 0
          ? { gridColumn: "span 7", gridRow: "span 6" }
          : { gridColumn: "span 3", gridRow: "span 6" },
    },
    ...destIds.map((id, idx) => ({
      id,
      ...DEST_DEF[id],
      label: screenTitle(scheme, id, DEST_DEF[id].label),
      go: id as Screen,
      span: destSpan(idx, destIds.length),
    })),
  ];
  // D-pad graph, generated from the SAME row breakdown the spans use
  // ({@link destRows}) rather than spelled out per tile: hotel sits to the left
  // of every first-column tile, left/right walks a row, up/down steps between
  // rows at the nearest column. Hand-written entries only covered three
  // destinations, so a fourth tile drew fine and could not be reached.
  const nav = useMemo<NavMap>(() => {
    const rows = destRows(destIds.length);
    const map: NavMap = { hotel: {} };
    if (destIds[0]) map.hotel.right = destIds[0];
    // The promo banner floats over the foot of the tall Hotel tile, so Down
    // from Hotel reaches it and Up comes back. Only when it holds a live offer:
    // a d-pad wall is better than a cell that draws nothing.
    if (hasBanner) {
      map.hotel.down = PROMO_CELL;
      map[PROMO_CELL] = { up: "hotel" };
    }
    rows.forEach((row, r) => {
      row.forEach((idx, c) => {
        const id = destIds[idx];
        const entry: NavMap[string] = {};
        const left = row[c - 1];
        entry.left = left === undefined ? "hotel" : destIds[left];
        if (row[c + 1] !== undefined) entry.right = destIds[row[c + 1]];
        const above = rows[r - 1];
        if (above) entry.up = destIds[above[Math.min(c, above.length - 1)]];
        const below = rows[r + 1];
        if (below) entry.down = destIds[below[Math.min(c, below.length - 1)]];
        map[id] = entry;
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destIds.join("|"), hasBanner]);
  // TV is a launch action, not a sub-page (DEMO-1) - same rule the launcher
  // templates follow. Screen Share tries the device's own receiver first and
  // only falls back to the instructions page when there is nothing to open.
  // Shared by the d-pad (`onEnter`) and the mouse (`onClick`) paths below.
  const pressDest = (id: string) => {
    if (id === "tv") {
      // `openBuiltin` is what gives the premise switch teeth: a premise on
      // `tvPlayer: "foyer"` opens our own player, one on "norigin" (the
      // default) still hands the screen to the vendor app.
      //
      // 🚨 It opens the PLAYER, not the guide. Dropping a guest straight into
      // the channel list is the behaviour `1fd8e91` replaced — "a channel
      // list, not a television: landing in one leaf of a structure with no way
      // to reach the others reads as a broken app rather than a smaller one".
      // Only the launcher kit was migrated then; this template kept the
      // superseded call, so one button meant two things depending on which
      // design a premise had chosen.
      openTvForThisScreen({
        openBuiltin: () => go("tv"),
        // The premise's choice from the config this render is using — the CMS
        // preview has one and no pairing binding to read a cache with.
        tvPlayer: scheme.context.tvPlayer,
      });
      return;
    }
    if (id === "cast") {
      if (tryOpenScreenShare()) return;
      go("cast");
      return;
    }
    go(id as Screen);
  };
  const [focus, setFocus] = useFocusNav({
    map: nav,
    // Still the Hotel tile. The banner is an offer nobody asked for; it may
    // interrupt the eye, never the remote (`docs/room-offers.md` §12).
    initial: "hotel",
    scopeRef,
    // The promo cell is NOT a screen id, so it is handled before the cast and
    // never falls through to `go()`. A `go("homePromo")` matches no `screen ===`
    // branch and would leave the guest on an empty page.
    onEnter: (id) => {
      if (id === PROMO_CELL) {
        if (banner) openOffer(banner.offer.id);
        return;
      }
      pressDest(id);
    },
  });
  // The banner can go away under the ring - its window closes, the offer is
  // deleted, the feed comes back without it - and `useFocusNav` walls every
  // direction for an id that is no longer in the map. Pull focus back to the
  // anchor tile rather than strand the remote on a cell that draws nothing.
  useEffect(() => {
    if (!hasBanner && focus === PROMO_CELL) setFocus("hotel");
  }, [hasBanner, focus, setFocus]);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />
      <div style={{ padding: "0 32px 18px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
          <h1
            style={{
              margin: 0,
              fontFamily: M.serif,
              fontWeight: 400,
              fontSize: 56,
              lineHeight: 0.95,
              letterSpacing: "-0.01em",
            }}
          >
            Welcome{" "}
            <em style={{ fontStyle: "italic", color: M.brick }}>back</em>.
            <span
              style={{
                marginLeft: 12,
                fontSize: 22,
                fontStyle: "italic",
                color: M.taupe,
                letterSpacing: 0,
              }}
            >
              We&apos;ve laid a few things out for you.
            </span>
          </h1>
        </div>
      </div>
      <div
        style={{
          flex: 1,
          padding: `0 ${PAGE_X}px 80px`,
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          // Six rows rather than four: 6 divides by 1, 2 AND 3, so the right
          // region can hold one, two or three rows of destination tiles without
          // a fractional span. See destRows.
          gridTemplateRows: "repeat(6, 1fr)",
          gap: 14,
          minHeight: 0,
        }}
      >
        {tiles.map((t, i) => {
          const active = focus === t.id;
          return (
            <button
              key={t.id}
              onMouseEnter={() => setFocus(t.id)}
              onClick={() => pressDest(t.id)}
              style={{
                appearance: "none",
                border: 0,
                padding: "22px 24px",
                // The hotel tile carries the premise photo (when set) under a
                // navy scrim; every other tile keeps its solid colour block.
                background:
                  t.id === "hotel" && hotelPhoto
                    ? `linear-gradient(180deg, rgba(26,29,61,${
                        bgL.tint ? 0.62 : 0.42
                      }) 0%, rgba(26,29,61,${
                        bgL.tint ? 0.9 : 0.72
                      }) 100%), url(${hotelPhoto}) center/cover`
                    : t.bg,
                color: t.fg,
                textAlign: "left",
                fontFamily: "inherit",
                cursor: "pointer",
                borderRadius: 18,
                position: "relative",
                overflow: "hidden",
                ...t.span,
                boxShadow: active
                  ? `0 0 0 3px ${M.ink}, 0 22px 40px -16px rgba(26,29,61,0.35)`
                  : `0 8px 20px -10px rgba(26,29,61,0.18)`,
                transform: active ? "translateY(-2px)" : "none",
                transition:
                  "transform 220ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 220ms",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <span
                  style={{
                    fontFamily: M.mono,
                    fontSize: 11,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    opacity: 0.7,
                  }}
                >
                  0{i + 1} · {t.id}
                </span>
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background:
                      t.id === "hotel"
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(26,29,61,0.10)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                  }}
                >
                  {t.figure}
                </span>
              </div>
              {t.id === "hotel" && (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 10,
                    marginTop: -20,
                  }}
                >
                  {[
                    "Order to the room",
                    "Book the spa",
                    "Late check-out",
                    "View the bill",
                  ].map((s) => (
                    <div
                      key={s}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        background: "rgba(255,255,255,0.08)",
                        borderRadius: 10,
                        border: "0.5px solid rgba(255,255,255,0.14)",
                        fontSize: 13,
                      }}
                    >
                      <span>{s}</span>
                      <span style={{ opacity: 0.7 }}>›</span>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div
                  style={{
                    fontFamily: M.serif,
                    fontWeight: 400,
                    fontSize: t.id === "hotel" ? 80 : 56,
                    lineHeight: 0.92,
                    letterSpacing: "-0.02em",
                    marginBottom: 4,
                  }}
                >
                  {t.label}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    opacity: 0.78,
                    lineHeight: 1.4,
                    maxWidth: 360,
                  }}
                >
                  {t.sub}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {/* operator promo slot - floats bottom-left above the footer strip */}
      <SchemeSlot
        slotId={PROMO_CELL}
        focused={focus === PROMO_CELL}
        onPress={openOffer}
        style={{
          bg: M.cream,
          ink: M.ink,
          inkDim: M.taupe,
          accent: M.brick,
          rule: M.rule,
          font: M.sans,
          kickerFont: M.mono,
          radius: 14,
        }}
        containerStyle={{
          position: "absolute",
          left: PAGE_X,
          // 80, not 64: a square this tall reaches the navy tile's rounded
          // bottom corner and hung ~15px past it. The strip never did.
          bottom: 80,
          // 360, not 540: the card is a SQUARE now, so its width sets its
          // height too, and 540 stood tall enough to bury the Hotel tile's own
          // link list behind it. This keeps the offer inside the navy tile's
          // lower half, clear of the links, and still the biggest single thing
          // on the bento after the tiles themselves.
          width: 360,
          zIndex: 6,
        }}
      />
      <Footer items={FOOTER_KEYS.slice(0, 2)} context={context} />
    </div>
  );
}

function Apps({ go, scopeRef, context }: ScreenProps) {
  const M = useM();
  const { content } = useScheme();
  const items = [
    { id: "hotel", name: "Hotel", isHotel: true as const },
    ...content.apps
      .slice(0, 11)
      .map((a) => ({ ...a, isHotel: false as const })),
  ];
  const cols = 4;
  const rows = 3;
  const [focus, setFocus] = useFocusGrid({
    rows,
    cols,
    scopeRef,
    onEnter: (f) => {
      const it = items[f.r * cols + f.c];
      if (!it) return;
      if (it.isHotel) go("hotel");
      else launchApp(it);
    },
    onBack: () => go("home"),
    wrap: false,
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Apps" />
      <div
        style={{
          flex: 1,
          padding: `6px ${PAGE_X}px 80px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: M.serif,
              fontWeight: 400,
              fontSize: 52,
              letterSpacing: "-0.01em",
            }}
          >
            <em style={{ fontStyle: "italic", color: M.brick }}>Twelve</em> apps
            in residence.
          </h2>
          <span
            style={{
              fontFamily: M.mono,
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: M.taupe,
            }}
          >
            Signed in to all
          </span>
        </div>
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            // `1fr` rows would stretch the cards and override their 16:9.
            gridAutoRows: "auto",
            alignContent: "start",
            gap: 14,
            minHeight: 0,
          }}
        >
          {items.map((it, i) => {
            const r = Math.floor(i / cols);
            const c = i % cols;
            const active = focus.r === r && focus.c === c;
            return (
              <AppCard
                key={it.id}
                app={it}
                active={active}
                onMouseEnter={() => setFocus({ r, c })}
                onClick={() => (it.isHotel ? go("hotel") : launchApp(it))}
              />
            );
          })}
        </div>
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

interface AppCardProps {
  app:
    | { id: string; name: string; isHotel: true }
    | (ResolvedApp & { isHotel: false });
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

function AppCard({ app, active, onMouseEnter, onClick }: AppCardProps) {
  const M = useM();
  const isHotel = app.isHotel;
  return (
    <button
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        appearance: "none",
        border: 0,
        padding: 0,
        cursor: "pointer",
        textAlign: "left",
        background: isHotel ? M.navy : app.color || "#222",
        color: isHotel ? M.paper : app.text || "#fff",
        position: "relative",
        // 16:9 - the frame the certification describes (NFLX-5).
        aspectRatio: "16 / 9",
        // border-box, or the ratio governs the CONTENT box and the padding is
        // added on top - which renders 1.65:1, not 16:9. Measured, not assumed.
        boxSizing: "border-box",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: active
          ? `0 0 0 3px ${M.ink}, 0 18px 32px -10px rgba(26,29,61,0.4)`
          : `0 6px 18px -8px rgba(26,29,61,0.22)`,
        // Signature micro-tilt - Mosaic only.
        transform: active ? "translateY(-3px) rotate(-0.4deg)" : "none",
        transition:
          "transform 220ms cubic-bezier(0.2,0.7,0.2,1), box-shadow 220ms",
        fontFamily: M.sans,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          padding: "14px 16px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span
            style={{
              fontSize: 11,
              fontFamily: M.mono,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              opacity: 0.7,
            }}
          >
            {isHotel ? "House" : "App"}
          </span>
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
            }}
          >
            {isHotel ? "✿" : "▸"}
          </span>
        </div>
        <div
          style={{
            fontFamily: isHotel ? M.serif : "inherit",
            fontSize: isHotel ? 38 : 22,
            fontWeight: isHotel ? 400 : 700,
            letterSpacing: "-0.02em",
          }}
        >
          {isHotel ? (
            "Hotel"
          ) : (app as ResolvedApp).logoUrl ? (
            <img
              src={(app as ResolvedApp).logoUrl}
              alt={app.name}
              style={{
                maxHeight: (app as ResolvedApp).certified
                  ? CERTIFIED_LOGO_MAX_HEIGHT
                  : 48,
                maxWidth: (app as ResolvedApp).certified
                  ? CERTIFIED_LOGO_MAX_WIDTH
                  : "90%",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            (app as ResolvedApp).wordmark
          )}
        </div>
      </div>
    </button>
  );
}

function Info({ go, scopeRef, context }: ScreenProps) {
  const M = useM();
  const [tab, setTab] = useState<"directory" | "welcome">("directory");
  const tabs: ("directory" | "welcome")[] = ["directory", "welcome"];
  const [focus, setFocus] = useFocusGrid({
    rows: 1,
    cols: 2,
    scopeRef,
    onEnter: (f) => setTab(tabs[f.c]),
    onBack: () => go("home"),
  });
  useEffect(() => {
    setFocus({ r: 0, c: tabs.indexOf(tab) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Information" />
      <div
        style={{
          flex: 1,
          padding: `6px ${PAGE_X}px 80px`,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: M.serif,
              fontWeight: 400,
              fontSize: 52,
              letterSpacing: "-0.01em",
            }}
          >
            The <em style={{ fontStyle: "italic", color: M.brick }}>house</em>{" "}
            guide.
          </h2>
          <div style={{ display: "flex", gap: 6 }}>
            {tabs.map((t, i) => {
              const active = tab === t;
              const focused = focus.c === i;
              return (
                <button
                  key={t}
                  onMouseEnter={() => setFocus({ r: 0, c: i })}
                  onClick={() => setTab(t)}
                  style={{
                    appearance: "none",
                    border: 0,
                    padding: "10px 18px",
                    background: active ? M.ink : M.paper2,
                    color: active ? M.paper : M.ink,
                    borderRadius: 999,
                    cursor: "pointer",
                    fontFamily: M.serif,
                    fontStyle: "italic",
                    fontSize: 18,
                    boxShadow:
                      focused && !active
                        ? `inset 0 0 0 2px ${M.brick}`
                        : "none",
                  }}
                >
                  {t === "directory" ? "Directory" : "Welcome floor"}
                </button>
              );
            })}
          </div>
        </div>
        {tab === "directory" && <Directory />}
        {tab === "welcome" && <Welcome context={context} />}
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

function Directory() {
  const M = useM();
  const { content } = useScheme();
  const tints = [M.cream, M.sage, M.blush, M.butter, M.paper2];
  return (
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gridAutoRows: "min-content",
        gap: 14,
        alignContent: "start",
        overflow: "hidden",
      }}
    >
      {content.infoSections.map((sec, i) => (
        <div
          key={sec.section}
          style={{
            padding: "18px 20px",
            background: tints[i % tints.length],
            borderRadius: 18,
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 10,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: M.serif,
                fontWeight: 400,
                fontSize: 30,
                letterSpacing: "-0.01em",
              }}
            >
              {sec.section}
            </h3>
            <span
              style={{
                fontFamily: M.mono,
                fontSize: 10,
                letterSpacing: "0.14em",
                color: M.taupe,
              }}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {sec.items.map((it) => (
              <div
                key={it.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  fontSize: 13,
                }}
              >
                <span style={{ color: M.taupe }}>{it.label}</span>
                <span style={{ textAlign: "right", color: M.ink }}>
                  {it.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Welcome({ context }: { context: SchemeContextValue }) {
  const M = useM();
  return (
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "1.3fr 1fr",
        gap: 18,
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: "relative",
          background: M.paper2,
          borderRadius: 22,
          overflow: "hidden",
          padding: 24,
        }}
      >
        <div
          style={{
            fontFamily: M.mono,
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: M.taupe,
          }}
        >
          Ground floor · plan
        </div>
        <svg
          viewBox="0 0 1000 560"
          style={{
            width: "100%",
            height: "calc(100% - 24px)",
            display: "block",
            marginTop: 8,
          }}
        >
          <rect x="80" y="60" width="180" height="180" fill={M.cream} rx="8" />
          <rect
            x="280"
            y="60"
            width="240"
            height="180"
            fill={M.butter}
            rx="8"
          />
          <rect x="540" y="60" width="180" height="280" fill={M.sage} rx="8" />
          <rect x="740" y="60" width="180" height="280" fill={M.blush} rx="8" />
          <rect
            x="80"
            y="260"
            width="180"
            height="240"
            fill={M.paper}
            rx="8"
            stroke={M.rule}
            strokeWidth="1"
          />
          <rect
            x="280"
            y="260"
            width="240"
            height="240"
            fill={M.cream}
            rx="8"
          />
          <rect
            x="540"
            y="360"
            width="380"
            height="140"
            fill={M.brick}
            rx="8"
          />
          {[
            { l: "Lobby", x: 170, y: 150 },
            { l: "Lobby Bar", x: 400, y: 150 },
            { l: "Restaurant", x: 630, y: 200 },
            { l: "Library", x: 830, y: 200 },
            { l: "Reception", x: 170, y: 380 },
            { l: "Grand Hall", x: 400, y: 380 },
            { l: "Gym · access ⇣", x: 730, y: 430, fill: "#fff" },
          ].map((r) => (
            <text
              key={r.l}
              x={r.x}
              y={r.y}
              textAnchor="middle"
              style={{
                fontFamily: "Instrument Serif, Playfair Display, serif",
                fontSize: 24,
                fontStyle: "italic",
                fill: r.fill ?? M.ink,
              }}
            >
              {r.l}
            </text>
          ))}
          <path
            d="M170 380 Q 270 380 280 320 Q 290 260 380 220 Q 420 190 400 170"
            stroke={M.brick}
            strokeWidth="2"
            strokeDasharray="6 6"
            fill="none"
          />
          <circle cx="170" cy="380" r="6" fill={M.brick} />
          <circle cx="400" cy="170" r="6" fill={M.brick} />
        </svg>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minHeight: 0,
        }}
      >
        <div
          style={{
            padding: 22,
            background: M.navy,
            color: M.paper,
            borderRadius: 18,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontFamily: M.mono,
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: M.butter,
              marginBottom: 8,
            }}
          >
            Welcome floor
          </div>
          <h3
            style={{
              margin: 0,
              fontFamily: M.serif,
              fontWeight: 400,
              fontSize: 38,
              lineHeight: 1.0,
              letterSpacing: "-0.02em",
            }}
          >
            Make yourself <em style={{ color: M.butter }}>at home</em>.
          </h3>
          <p
            style={{
              marginTop: 10,
              fontSize: 13,
              lineHeight: 1.5,
              color: "rgba(250,248,243,0.78)",
            }}
          >
            The lobby bar pours until 1 a.m.; the spa is quiet after nine; the
            rooftop garden opens at sunrise.
          </p>
          {context.roomId && (
            <p
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "rgba(250,248,243,0.55)",
              }}
            >
              You&apos;re in room {context.roomId}.
            </p>
          )}
        </div>
        <div
          style={{
            padding: 18,
            background: M.butter,
            color: M.ink,
            borderRadius: 18,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontFamily: M.mono,
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                opacity: 0.7,
              }}
            >
              Wi-Fi
            </div>
            <div
              style={{
                fontFamily: M.serif,
                fontSize: 26,
                marginTop: 2,
              }}
            >
              NendaGuest
            </div>
          </div>
          <div
            style={{
              padding: "8px 12px",
              background: M.ink,
              color: M.paper,
              borderRadius: 10,
              fontFamily: M.mono,
              fontSize: 14,
              letterSpacing: "0.04em",
            }}
          >
            welcome2026
          </div>
        </div>
        <div
          style={{
            padding: 18,
            background: M.sage,
            color: M.ink,
            borderRadius: 18,
          }}
        >
          <div
            style={{
              fontFamily: M.mono,
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              opacity: 0.7,
            }}
          >
            This evening
          </div>
          <div
            style={{
              fontFamily: M.serif,
              fontSize: 26,
              marginTop: 2,
              lineHeight: 1.1,
            }}
          >
            <em>Sea bass</em> at Lumen
            <br />· dinner from 18:00
          </div>
        </div>
      </div>
    </div>
  );
}

function Radio({ go, scopeRef, context }: ScreenProps) {
  const M = useM();
  const stations = useScheme().content.stations;
  // Audio playback (start on OK / stop on leave); `playingId` null until OK.
  const { playingId, toggle, audioRef } = useRadioPlayback(stations);
  const [focus, setFocus] = useFocusGrid({
    rows: stations.length,
    cols: 1,
    scopeRef,
    onEnter: (f) => toggle(stations[f.r]?.id),
    onBack: () => go("home"),
  });
  // The hero PREVIEWS the focused (hovered) station; OK starts playback.
  const station = stations[focus.r] ?? stations[0];
  const isPlaying = !!station && station.id === playingId;
  const np = useRadioNowPlaying(stations.map((s) => s.metadataId));
  const live = station?.metadataId ? np[station.metadataId] : undefined;
  const program = live?.program ?? station?.nowPlaying;
  const song = live?.song;
  const rowRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [focus.r]);
  const hue = stationHue(station.id);
  const tintBase = `hsl(${hue}, 60%, 88%)`;
  const tintDark = `hsl(${hue}, 50%, 30%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <audio ref={audioRef} preload="none" />
      <Header crumb="Radio" />
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
          padding: `6px ${PAGE_X}px 80px`,
          minHeight: 0,
        }}
      >
        <div
          style={{
            background: tintBase,
            borderRadius: 22,
            padding: 28,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            color: tintDark,
            overflow: "hidden",
            position: "relative",
            transition: "background 600ms ease, color 600ms ease",
          }}
        >
          <div>
            <div
              style={{
                fontFamily: M.mono,
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: isPlaying ? tintDark : "transparent",
                  border: isPlaying ? "none" : `1.5px solid ${tintDark}`,
                  animation: isPlaying ? "pulseGlow 1.6s infinite" : "none",
                }}
              />
              {isPlaying ? "On air" : "Preview"}
            </div>
            <h2
              style={{
                margin: "14px 0 6px",
                fontFamily: M.serif,
                fontWeight: 400,
                fontSize: 78,
                lineHeight: 0.9,
                letterSpacing: "-0.02em",
                color: tintDark,
              }}
            >
              {station.name}
            </h2>
            <div
              style={{
                fontSize: 16,
                fontStyle: "italic",
                fontFamily: M.serif,
              }}
            >
              {station.tag} · {station.freq}
            </div>
          </div>
          <div
            style={{
              position: "absolute",
              right: -100,
              bottom: -100,
              width: 360,
              height: 360,
              borderRadius: "50%",
              background: `radial-gradient(circle at 50% 50%, ${tintDark} 0 4%, ${M.ink} 4.5% 100%)`,
              boxShadow: "0 30px 60px -20px rgba(0,0,0,0.4)",
            }}
          >
            {[40, 60, 80, 100].map((r) => (
              <div
                key={r}
                style={{
                  position: "absolute",
                  inset: 0,
                  margin: "auto",
                  width: `${r}%`,
                  height: `${r}%`,
                  borderRadius: "50%",
                  boxShadow: `inset 0 0 0 0.5px hsla(${hue},50%,60%,0.25)`,
                }}
              />
            ))}
          </div>
          <div
            style={{
              marginTop: 24,
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              style={{
                fontFamily: M.mono,
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                opacity: 0.7,
              }}
            >
              {isPlaying ? "Now playing" : "Preview"}
            </div>
            <div
              style={{
                fontFamily: M.serif,
                fontStyle: "italic",
                fontSize: 32,
                lineHeight: 1.1,
                marginTop: 4,
                maxWidth: 360,
              }}
            >
              {program}
            </div>
            {song && (
              <div
                style={{
                  fontFamily: M.mono,
                  fontSize: 13,
                  marginTop: 6,
                  opacity: 0.75,
                }}
              >
                ♪ {song}
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            background: M.paper2,
            borderRadius: 22,
            padding: "14px 0",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div
            style={{
              padding: "4px 22px 10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <span
              style={{
                fontFamily: M.serif,
                fontStyle: "italic",
                fontSize: 22,
              }}
            >
              Stations
            </span>
            <span
              style={{
                fontFamily: M.mono,
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: M.taupe,
              }}
            >
              {stations.length} · curated
            </span>
          </div>
          <div
            className="foyer-scheme-scroll"
            style={{
              flex: 1,
              overflow: "auto",
              padding: "4px 14px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {stations.map((s, i) => {
                const active = focus.r === i;
                const rowPlaying = playingId === s.id;
                return (
                  <button
                    key={s.id}
                    ref={active ? rowRef : undefined}
                    onMouseEnter={() => setFocus({ r: i, c: 0 })}
                    onClick={() => toggle(s.id)}
                    style={{
                      appearance: "none",
                      border: 0,
                      padding: "10px 12px",
                      textAlign: "left",
                      display: "grid",
                      gridTemplateColumns: "32px 1fr auto",
                      gap: 12,
                      alignItems: "center",
                      background: active ? M.paper : "transparent",
                      borderRadius: 12,
                      cursor: "pointer",
                      color: "inherit",
                      fontFamily: "inherit",
                      boxShadow: active ? `inset 0 0 0 2px ${M.ink}` : "none",
                      transition: "all 160ms",
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: `hsl(${stationHue(s.id)},60%,72%)`,
                        boxShadow: rowPlaying
                          ? `0 0 0 3px hsl(${stationHue(s.id)},60%,52%)`
                          : "none",
                      }}
                    />
                    <span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: M.serif,
                            fontSize: 22,
                            color: M.ink,
                          }}
                        >
                          {s.name}
                        </span>
                        {rowPlaying && (
                          <span
                            style={{
                              fontFamily: M.mono,
                              fontSize: 9,
                              padding: "2px 6px",
                              borderRadius: 999,
                              background: M.ink,
                              color: M.paper,
                              letterSpacing: "0.16em",
                              textTransform: "uppercase",
                            }}
                          >
                            On air
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: M.taupe,
                        }}
                      >
                        {s.tag}
                      </div>
                    </span>
                    <span
                      style={{
                        fontFamily: M.mono,
                        fontSize: 11,
                        color: M.taupe,
                      }}
                    >
                      {s.freq}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

function HotelLaunch({
  go,
  scopeRef,
  context,
}: {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}) {
  const M = useM();
  // The hotel screen's only action is to return home. Wire OK and the Back key
  // to it and keep the Back control focused so the remote can always escape.
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onEnter: () => go("home"),
    onBack: () => go("home"),
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: M.navy,
        color: M.paper,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "20px 32px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Logo variant="light" height={22} />
        <button
          onClick={() => go("home")}
          style={{
            appearance: "none",
            border: `1px solid ${M.butter}`,
            background: "rgba(255,216,107,0.14)",
            color: M.paper,
            padding: "6px 14px",
            borderRadius: 999,
            fontFamily: "inherit",
            fontSize: 12,
            cursor: "pointer",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            boxShadow: "0 0 0 3px rgba(255,216,107,0.28)",
          }}
        >
          ← Back
        </button>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: "0 80px",
        }}
      >
        <div
          style={{
            fontFamily: M.mono,
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: M.butter,
            marginBottom: 14,
          }}
        >
          Concierge · opening
        </div>
        <h1
          style={{
            margin: 0,
            fontFamily: M.serif,
            fontWeight: 400,
            fontSize: 92,
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
          }}
        >
          {context.hotelName.split(" ")[0] ?? "The hotel"}{" "}
          <em style={{ fontStyle: "italic", color: M.butter }}>
            at your service
          </em>
          .
        </h1>
        <p
          style={{
            marginTop: 16,
            fontSize: 16,
            color: "rgba(250,248,243,0.7)",
            maxWidth: 580,
            lineHeight: 1.55,
          }}
        >
          Order to the room, book the spa, request housekeeping, settle the
          bill, or extend the stay - everything the front desk can do, on the
          screen.
        </p>
      </div>
    </div>
  );
}

/**
 * Screen Share (DEMO-1). Reached only as the FALLBACK: the "Screen Share"
 * tile tries `tryOpenScreenShare()` first and only calls `go("cast")` when
 * there is nothing to open (a set-top box entered from its own source list,
 * the CMS preview). Pure instruction, never the casting UI itself - see
 * SpectrumScheme.tsx's `Cast` for the fuller reasoning.
 */
function Cast({
  go,
  scopeRef,
  context,
}: {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}) {
  const M = useM();
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onEnter: () => go("home"),
    onBack: () => go("home"),
  });
  const room = context.roomId ? `Room ${context.roomId}` : "Guest TV";
  const canCast = bridge.capabilities().guestCasting;
  const castInfo = bridge.getCastingInfo();
  const castName = castInfo.deviceName?.trim() || room;
  const steps = castSteps({
    info: canCast ? castInfo : null,
    deviceName: castName,
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Screen Share" />
      <div style={{ padding: "0 32px 18px" }}>
        <h1
          style={{
            margin: 0,
            fontFamily: M.serif,
            fontWeight: 400,
            fontSize: 44,
            letterSpacing: "-0.01em",
          }}
        >
          Share your <em style={{ fontStyle: "italic" }}>screen</em>.
        </h1>
        <p style={{ margin: "8px 0 0", fontSize: 14, color: M.taupe }}>
          Mirror your phone, tablet or laptop onto this television.
        </p>
      </div>
      <div
        style={{
          flex: 1,
          padding: `0 ${PAGE_X}px 80px`,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 14,
          minHeight: 0,
        }}
      >
        {steps.map((s) => (
          <div
            key={s.n}
            style={{
              background: M.paper2,
              border: `1px solid ${M.rule}`,
              borderRadius: 16,
              padding: "24px 22px",
            }}
          >
            <div
              style={{
                fontFamily: M.serif,
                fontSize: 30,
                fontWeight: 400,
                color: M.brick,
              }}
            >
              {s.n}
            </div>
            <div
              style={{
                fontFamily: M.serif,
                fontSize: 22,
                marginTop: 4,
              }}
            >
              {s.title}
            </div>
            <div style={{ fontSize: 13.5, color: M.taupe, marginTop: 6 }}>
              {s.detail}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
